import impuestosService from "@/services/impuestosService";

/**
 * Vuex Module — Catálogo de Impuestos + Asignación Masiva (Trello #306 — PR5)
 * ─────────────────────────────────────────────────────────────────────────
 * Gestiona el catálogo de impuestos/alícuotas (ABM) y las reglas de
 * asignación masiva por alcance. El preview de una asignación masiva
 * NUNCA escribe — la UI debe pedirlo y mostrar confirmación antes de
 * llamar a aplicarAsignacion (spec/design: "preview obligatorio").
 */

const state = () => ({
  catalogo: [],
  asignaciones: [],
  preview: null,
  loading: false,
  error: null,
  // Judgment Day PR5 (micro-ronda final) fix #2: error dedicado del catálogo,
  // seteado SOLO por fetchCatalogo y limpiado al inicio de cada fetch — así
  // un fallo de preview/aplicar/toggle (que siguen usando `error`) no
  // contamina el banner del catálogo en ImpuestosList.
  errorCatalogo: null,
});

const getters = {
  catalogo: (state) => state.catalogo,
  asignaciones: (state) => state.asignaciones,
  preview: (state) => state.preview,
  isLoading: (state) => state.loading,
  hasError: (state) => state.error,
  errorCatalogo: (state) => state.errorCatalogo,
  // Alícuotas activas del impuesto de sistema IVA — usado por FacturacionManual
  // y por la solapa de impuestos del producto para poblar selectores.
  alicuotasIvaActivas: (state) => {
    const iva = state.catalogo.find((impuesto) => impuesto.codigo === "IVA");
    if (!iva) return [];
    return (iva.alicuotas || []).filter((alicuota) => alicuota.activo);
  },
};

const mutations = {
  SET_CATALOGO(state, catalogo) {
    state.catalogo = catalogo;
  },
  SET_ASIGNACIONES(state, asignaciones) {
    state.asignaciones = asignaciones;
  },
  SET_PREVIEW(state, preview) {
    state.preview = preview;
  },
  SET_LOADING(state, value) {
    state.loading = value;
  },
  SET_ERROR(state, value) {
    state.error = value;
  },
  SET_ERROR_CATALOGO(state, value) {
    state.errorCatalogo = value;
  },
};

const actions = {
  async fetchCatalogo({ commit }) {
    // Fix #2 (Judgment Day PR5, micro-ronda final): limpiar errorCatalogo al
    // inicio de cada fetch para que un banner de un intento anterior no
    // sobreviva mientras el nuevo request está en vuelo.
    commit("SET_ERROR_CATALOGO", null);
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.getCatalogo();
      commit("SET_CATALOGO", data.catalogo || []);
      commit("SET_ERROR", null);
      return data.catalogo;
    } catch (error) {
      const mensaje = error.message || "Error al cargar el catálogo de impuestos";
      commit("SET_ERROR", mensaje);
      commit("SET_ERROR_CATALOGO", mensaje);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async crearImpuesto({ commit, dispatch }, payload) {
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.crearImpuesto(payload);
      await dispatch("fetchCatalogo");
      return data.impuesto;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al crear el impuesto");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async actualizarImpuesto({ commit, dispatch }, { idImpuesto, data: payload }) {
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.actualizarImpuesto(idImpuesto, payload);
      await dispatch("fetchCatalogo");
      return data.impuesto;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al actualizar el impuesto");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async eliminarImpuesto({ commit, dispatch }, idImpuesto) {
    try {
      commit("SET_LOADING", true);
      await impuestosService.eliminarImpuesto(idImpuesto);
      await dispatch("fetchCatalogo");
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al eliminar el impuesto");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async crearAlicuota({ commit, dispatch }, { idImpuesto, data: payload }) {
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.crearAlicuota(idImpuesto, payload);
      await dispatch("fetchCatalogo");
      return data.alicuota;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al crear la alícuota");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async actualizarAlicuota({ commit, dispatch }, { idImpuesto, idAlicuota, data: payload }) {
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.actualizarAlicuota(idImpuesto, idAlicuota, payload);
      await dispatch("fetchCatalogo");
      return data.alicuota;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al actualizar la alícuota");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async eliminarAlicuota({ commit, dispatch }, { idImpuesto, idAlicuota }) {
    try {
      commit("SET_LOADING", true);
      await impuestosService.eliminarAlicuota(idImpuesto, idAlicuota);
      await dispatch("fetchCatalogo");
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al eliminar la alícuota");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchAsignaciones({ commit }) {
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.getAsignaciones();
      commit("SET_ASIGNACIONES", data.asignaciones || []);
      return data.asignaciones;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al cargar las reglas de asignación");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Nunca escribe en el backend — la UI usa este resultado para exigir
  // confirmación explícita antes de dispatch('aplicarAsignacion').
  async previewAsignacion({ commit }, payload) {
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.previewAsignacion(payload);
      commit("SET_PREVIEW", data);
      return data;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al calcular el preview de asignación");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async aplicarAsignacion({ commit, dispatch }, payload) {
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.aplicarAsignacion(payload);
      commit("SET_PREVIEW", null);
      await dispatch("fetchAsignaciones");
      return data;
    } catch (error) {
      // Fallo parcial (Judgment Day PR4 fix #6): el error trae
      // aplicados_parciales/reintentable — se propaga tal cual para que la
      // UI ofrezca reintentar sin perder ese contexto.
      commit("SET_ERROR", error.message || "Error al aplicar la asignación masiva");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Asignación puntual (siempre origen='manual' en el backend).
  async asignarImpuestoProducto({ commit }, { productId, payload }) {
    try {
      commit("SET_LOADING", true);
      const { data } = await impuestosService.asignarImpuestoProducto(productId, payload);
      return data;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al asignar el impuesto al producto");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Limpieza explícita del preview en el store (Judgment Day PR5 fix #4): al
  // cerrar el diálogo de asignación masiva sin aplicar, el preview vigente
  // NO debe sobrevivir para la próxima apertura del diálogo.
  limpiarPreview({ commit }) {
    commit("SET_PREVIEW", null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
